import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from '@tecalisdev/infinity';
import jsQR from 'jsqr';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { CameraService } from 'src/app/shared/services/camera.service';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';

export interface MiDNIQrResult {
	/** Bytes crudos del QR codificados en base64 */
	qrBase64: string;
	/** Frame de la cámara en el momento de detección (JPEG base64, sin prefijo data URI) */
	frameBase64: string;
	/** Recorte de la zona del QR (JPEG base64, sin prefijo data URI) */
	qrImageBase64: string;
}

/**
 * Claves i18n de los mensajes de guía (3 mensajes = 3 locuciones).
 * PROMPT_4 se usa solo en errores: mismo mensaje y misma locución (PROMPT_4.mp3).
 */
/** Tras X s sin QR válido: pista UX sin clasificar tipo de QR (NIF-259). Luego el ciclo de guías NIF-86. */
const SCAN_PROMPTS = [
	'OCR.MIDNI.PROMPT_FULL_QR_REMINDER',
	'OCR.MIDNI.PROMPT_1',
	'OCR.MIDNI.PROMPT_2',
	'OCR.MIDNI.PROMPT_3',
] as const;
const SCAN_PROMPTS_MOBILE = [
	'OCR.MIDNI.PROMPT_FULL_QR_REMINDER',
	'OCR.MIDNI.PROMPT_1',
	'OCR.MIDNI.PROMPT_2',
	'OCR.MIDNI.PROMPT_5',
] as const;

/** Primer mensaje a los 13 s sin lectura válida; cada +13 s rota según SCAN_PROMPTS* (NIF-259, NIF-86). */
const PROMPT_INTERVAL_MS = 13000;
const CAMERA_PERMISSION_ERROR_I18N_KEY = 'OCR.MIDNI.CAMERA_PERMISSION_ERROR';

@Component({
	selector: 'app-midni-qr-scanner',
	standalone: true,
	imports: [TranslatePipe, SpinnerComponent, VideoComponent],
	templateUrl: './midni-qr-scanner.component.html',
	styleUrls: ['./midni-qr-scanner.component.scss'],
})
export class MidniQrScannerComponent implements OnDestroy {
	@Input({ required: true }) constraints!: MediaStreamConstraints;
	@Input() instructionI18nKey = 'OCR.MIDNI.SCANNER_INSTRUCTION';

	@Output() qrDetected = new EventEmitter<MiDNIQrResult>();
	/** Emite la clave i18n del mensaje de guía para que el padre gestione locución + texto */
	@Output() promptRequest = new EventEmitter<string>();
	@Output() cameraPermissionRevoked = new EventEmitter<void>();

	@ViewChild('canvasEl', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

	public cameraReady = false;
	public scanning = false;
	public errorMsg = '';
	/** Versión incrementada para recrear `app-video` en restart (misma clave que @for track). */
	public videoRemountVersion = 0;

	private stream: MediaStream | null = null;
	private scannerVideo: HTMLVideoElement | null = null;
	private animFrameId: number | null = null;
	private detected = false;
	private promptTimer: ReturnType<typeof setInterval> | null = null;
	private promptDelay: ReturnType<typeof setTimeout> | null = null;
	/** Índice rotativo sobre SCAN_PROMPTS / SCAN_PROMPTS_MOBILE */
	private promptIndex = 0;

	constructor(
		private ngZone: NgZone,
		private cameraService: CameraService,
		private userAgentService: UserAgentService,
		private trans: TranslateService,
	) {}

	ngOnDestroy(): void {
		this.stopCamera();
		this.stopPrompts();
	}

	/** Reinicia el escáner desde el exterior (p. ej., tras error de QR inválido) */
	public restart(): void {
		this.detected = false;
		this.errorMsg = '';
		this.promptIndex = 0;
		this.stopPrompts();
		this.stopCamera();
		this.videoRemountVersion++;
	}

	onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.stream = camera.stream;
		this.scannerVideo = camera.video;
		this.cameraReady = true;
		this.scanning = true;
		this.startPrompts();
		this.tick();
	}

	onCameraError(_err: unknown): void {
		this.errorMsg = this.trans.instant(CAMERA_PERMISSION_ERROR_I18N_KEY);
		this.cameraReady = false;
		this.scanning = false;
	}

	public onCameraPermissionRevoked(): void {
		this.stopPrompts();
		this.stopCamera();
		this.errorMsg = this.trans.instant(CAMERA_PERMISSION_ERROR_I18N_KEY);
		this.cameraPermissionRevoked.emit();
	}

	// ─── Detección de QR ──────────────────────────────────────────────────────

	private tick(): void {
		if (this.detected) return;

		const video = this.scannerVideo;
		const canvas = this.canvasRef?.nativeElement;
		if (!video || !canvas) {
			this.animFrameId = requestAnimationFrame(() => this.tick());
			return;
		}

		if (video.readyState === video.HAVE_ENOUGH_DATA) {
			const w = video.videoWidth;
			const h = video.videoHeight;

			canvas.width = w;
			canvas.height = h;

			const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
			ctx.drawImage(video, 0, 0, w, h);
			const imageData = ctx.getImageData(0, 0, w, h);

			const code = jsQR(imageData.data, w, h, { inversionAttempts: 'attemptBoth' });

			if (code) {
				let bytes: Uint8Array;
				if (code.binaryData?.length) {
					bytes = new Uint8Array(code.binaryData);
				} else if (code.data != null && code.data !== '') {
					bytes = new Uint8Array([...code.data].map((c) => c.charCodeAt(0) & 0xff));
				} else {
					bytes = new Uint8Array(0);
				}
				if (bytes.length > 0) {
					this.detected = true;
					this.stopPrompts();

					const frameBase64 = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];
					const qrImageBase64 = this.getQrCropBase64(canvas, code);
					this.stopCamera();

					const qrBase64 = btoa(String.fromCharCode(...bytes));
					this.ngZone.run(() => this.qrDetected.emit({ qrBase64, frameBase64, qrImageBase64 }));
					return;
				}
			}
		}

		this.animFrameId = requestAnimationFrame(() => this.tick());
	}

	private stopCamera(): void {
		if (this.animFrameId !== null) {
			cancelAnimationFrame(this.animFrameId);
			this.animFrameId = null;
		}
		this.stream?.getTracks().forEach((t) => t.stop());
		this.stream = null;
		this.scannerVideo = null;
		this.cameraService.stream = null;
		this.scanning = false;
		this.cameraReady = false;
	}

	// ─── Guía periódica delegada al padre ────────────────────────────────────

	private startPrompts(): void {
		this.promptDelay = setTimeout(() => {
			this.emitPromptCycle();
			this.promptTimer = setInterval(() => this.emitPromptCycle(), PROMPT_INTERVAL_MS);
		}, PROMPT_INTERVAL_MS);
	}

	private emitPromptCycle(): void {
		const prompts: readonly string[] = this.userAgentService.isMobile() ? SCAN_PROMPTS_MOBILE : SCAN_PROMPTS;
		const index = this.promptIndex % prompts.length;
		this.promptRequest.emit(prompts[index]);
		this.promptIndex++;
	}

	private stopPrompts(): void {
		if (this.promptDelay !== null) {
			clearTimeout(this.promptDelay);
			this.promptDelay = null;
		}
		if (this.promptTimer !== null) {
			clearInterval(this.promptTimer);
			this.promptTimer = null;
		}
	}

	private getQrCropBase64(canvas: HTMLCanvasElement, code: any): string {
		const location = code.location;
		const points = [
			location.topLeftCorner,
			location.topRightCorner,
			location.bottomLeftCorner,
			location.bottomRightCorner,
		];
		const xs = points.map((p) => p.x);
		const ys = points.map((p) => p.y);
		const minX = Math.max(0, Math.floor(Math.min(...xs)));
		const minY = Math.max(0, Math.floor(Math.min(...ys)));
		const maxX = Math.min(canvas.width, Math.ceil(Math.max(...xs)));
		const maxY = Math.min(canvas.height, Math.ceil(Math.max(...ys)));
		const rawW = Math.max(1, maxX - minX);
		const rawH = Math.max(1, maxY - minY);

		const padX = Math.floor(rawW * 0.12);
		const padY = Math.floor(rawH * 0.12);
		const cropX = Math.max(0, minX - padX);
		const cropY = Math.max(0, minY - padY);
		const cropW = Math.min(canvas.width - cropX, rawW + padX * 2);
		const cropH = Math.min(canvas.height - cropY, rawH + padY * 2);

		const qrCanvas = document.createElement('canvas');
		qrCanvas.width = Math.max(1, cropW);
		qrCanvas.height = Math.max(1, cropH);
		const qrCtx = qrCanvas.getContext('2d');
		if (!qrCtx) {
			return canvas.toDataURL('image/jpeg', 0.9).split(',')[1];
		}

		qrCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
		return qrCanvas.toDataURL('image/jpeg', 0.95).split(',')[1];
	}
}
